import { Link } from '@/i18n/routing';
import { Trail } from '@/types';
import {
  DifficultyBadge,
  StatusIcon,
  TrailStyleBadge,
  StatIcon,
  StarRating,
  difficultyColors
} from '@/components/icons';

interface TrailCardProps {
  trail: Trail;
  bikeparkSlug?: string;
}

export default function TrailCard({ trail, bikeparkSlug }: TrailCardProps) {
  const statusLabels: Record<string, string> = {
    open: 'Otwarta',
    closed: 'Zamknięta',
    maintenance: 'Konserwacja'
  };

  const trailUrl = trail.slug ? `/trasa/${trail.slug}` : '#';
  const hasValidUrl = !!trail.slug;

  const CardContent = (
    <div className="trail-card">
      <div className="trail-card-header">
        <div className="trail-name-row">
          <h3 className="trail-name">
            {trail.trailNumber && <span className="trail-number">{trail.trailNumber}</span>}
            {trail.name}
          </h3>
          {trail.status && trail.status !== 'open' && (
            <span className={`trail-status status-${trail.status}`}>
              <StatusIcon status={trail.status as 'open' | 'closed' | 'maintenance'} size={14} />
              <span className="ml-1">{statusLabels[trail.status]}</span>
            </span>
          )}
        </div>

        <div className="trail-badges">
          <span
            className="difficulty-badge"
            style={{
              backgroundColor: difficultyColors[trail.difficulty as keyof typeof difficultyColors] || '#gray',
              color: trail.difficulty === 'black' || trail.difficulty === 'double-black' ? '#fff' : '#000'
            }}
          >
            <DifficultyBadge
              difficulty={trail.difficulty as 'green' | 'blue' | 'red' | 'black' | 'double-black'}
              size={12}
            />
          </span>

          {trail.trailStyle && (
            <span className="style-badge">
              <TrailStyleBadge style={trail.trailStyle} size={14} />
            </span>
          )}
        </div>
      </div>

      <div className="trail-stats">
        {trail.length && (
          <div className="trail-stat">
            <span className="stat-icon"><StatIcon type="length" size={14} /></span>
            <span className="stat-value">{Math.round(trail.length)}m</span>
          </div>
        )}

        {trail.verticalDrop && (
          <div className="trail-stat">
            <span className="stat-icon"><StatIcon type="drop" size={14} /></span>
            <span className="stat-value">{Math.round(trail.verticalDrop)}m</span>
          </div>
        )}

        {trail.verticalClimb && trail.verticalClimb > 0 && (
          <div className="trail-stat">
            <span className="stat-icon"><StatIcon type="climb" size={14} /></span>
            <span className="stat-value">{Math.round(trail.verticalClimb)}m</span>
          </div>
        )}

        {trail.averageGradient && (
          <div className="trail-stat">
            <span className="stat-icon"><StatIcon type="gradient" size={14} /></span>
            <span className="stat-value">{trail.averageGradient.toFixed(1)}%</span>
          </div>
        )}

        {trail.estimatedRideTime && (
          <div className="trail-stat">
            <span className="stat-icon"><StatIcon type="time" size={14} /></span>
            <span className="stat-value">{trail.estimatedRideTime}min</span>
          </div>
        )}
      </div>

      {trail.features && trail.features.length > 0 && (
        <div className="trail-features">
          {trail.features.slice(0, 5).map((feature, idx) => (
            <span key={idx} className="feature-tag">{feature}</span>
          ))}
          {trail.features.length > 5 && (
            <span className="feature-tag">+{trail.features.length - 5}</span>
          )}
        </div>
      )}

      {(trail.flowScore || trail.technicalScore) && (
        <div className="trail-scores">
          {trail.flowScore && (
            <div className="score">
              <span className="score-label">Flow:</span>
              <div className="score-stars">
                <StarRating rating={trail.flowScore} size={14} />
              </div>
            </div>
          )}
          {trail.technicalScore && (
            <div className="score">
              <span className="score-label">Technika:</span>
              <div className="score-stars">
                <StarRating rating={trail.technicalScore} size={14} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return hasValidUrl ? (
    <Link href={trailUrl} className="trail-card-wrapper">
      {CardContent}
    </Link>
  ) : (
    <div className="trail-card-wrapper trail-card-no-link">
      {CardContent}
    </div>
  );
}
